import { Injectable } from '@angular/core';
import { LocalDate } from "../../../../../../core/pojo/date-variable";
import {
  CompanyCrossCompanyDTO,
  CrossCompanyResponse,
  MetricCrossCompanyDTO,
  MetricDataCrossCompanyDTO,
} from "./kpi-cross-company.service";
import { MetricDataType } from "../../../../../recovery/component/recovery-metrics-panel/entity/DefaultMetric";

@Injectable({
  providedIn: 'root',
})
export class KpiCrossCompanyHelperService {
  /**
   * Creates a new object, not modifying the old one, in which
   * 1. In any metric if a company has no data for all periods, it's removed from the list of
   * companies of this metric
   * 2. If any metric has no companies left, it's removed from the list of metrics
   * 3. If there are no metrics left, the response will be marked as empty data provided
   * 4. For every non-empty company there will be added missing periods from
   * `originalResponse.periods` containing `rowValue = null`
   *
   * @param originalResponse
   */
  enrichResponse(originalResponse: CrossCompanyResponse): CrossCompanyResponse {
    let isEmptyDataProvided = originalResponse.isEmptyDataProvided ?? false;

    const transformedMetrics = originalResponse.metrics.map((metric) => this.enrichMetric(metric, originalResponse.periods));

    const finalMetrics = transformedMetrics.filter(
      (metric) => metric.companies.length > 0
    );
    if (finalMetrics.length !== transformedMetrics.length) {
      isEmptyDataProvided = true;
    }

    return {
      ...originalResponse,
      metrics: finalMetrics,
      isEmptyDataProvided,
    };
  }

  /**
   * Creates a new object, not modifying the old one, in which
   * 1. If any company in the provided metric has no data for all periods, it's removed from the list of
   * companies
   * 2. For every non-empty company there will be added missing periods from `originalResponse.periods`
   * containing `rowValue = null`
   *
   * @param metric to enrich
   * @param periods that will be present in data of all companies of provided metric
   */
  enrichMetric(metric: MetricCrossCompanyDTO, periods: LocalDate[]): MetricCrossCompanyDTO {
    const filteredCompanies = metric.companies
      .filter((c) => c.data.filter((d) =>
        this.rowValueExists(d.rowValue, metric.dataProps.dataType)).length > 0)
      .map((company) => this.enrichCompany(company, periods));


    return {
      ...metric,
      showCompanies: true,
      companies: filteredCompanies,
    };
  }

  /**
   * Creates a new object, not modifying the old one, where it adds all missing periods from {@link periods}
   *
   * @param company - company to enrich
   * @param periods - periods that must be in resulting company data
   * @returns new company dto with missing periods
   */
  enrichCompany(company: CompanyCrossCompanyDTO, periods: LocalDate[]): CompanyCrossCompanyDTO {
    const existingByPeriod = new Map<string, MetricDataCrossCompanyDTO>();
    company.data.forEach((item) => {
      existingByPeriod.set(item.period.toString(), item);
    });
    const newData = periods.map((period) => {
      const existing = existingByPeriod.get(period.toString());
      return existing ? existing : { uuid: null, period, rowValue: null };
    });

    return {
      ...company,
      data: newData,
    };
  }

  rowValueExists(value: string, dataType: MetricDataType): boolean {
    // Special handling for Document type metrics
    if (dataType === MetricDataType.DOCUMENT) {
      return this.documentRowValueValueExists(value);
    }
    return value !== null && value !== '0' && value !== '' && value !== '0.0';
  }

  documentRowValueValueExists(value: string): boolean {
    if (!value) {
      return false;
    }
    return value.includes('\n');
  }

}
